'use client';
/**
 * contexts/MobileContext.tsx — OGRYN
 *
 * Contexte global de l'interface mobile.
 * Centralise : vue (carte/liste), onglet actif, état des overlays,
 * filtres de recherche, filtres sauvegardés, sous-filtres jobs/dispos.
 *
 * ⚠️  Aucun import Leaflet ici.
 */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  type ReactNode,
} from 'react';
import { trpc } from '@/lib/trpc/client';

export type ViewMode      = 'map' | 'list';
export type ActiveTab     = 'prestataires' | 'jobs' | 'dispos';
export type JobSubFilter  = 'all' | 'mission' | 'bon_plan';
export type DispoSubFilter = 'all' | 'materiel' | 'vehicule' | 'local';

export interface MobileFilters {
  ville:       string;
  metier:      string;
  distanceKm:  number;
  scoreMin:    number;
  vipOnly:     boolean;
}

export interface SavedFilter {
  name:      string;
  filters:   MobileFilters;
  isDefault: boolean;
  savedAt:   string; // ISO date
}

export const DEFAULT_FILTERS: MobileFilters = {
  ville:       '',
  metier:      '',
  distanceKm:  20,
  scoreMin:    0,
  vipOnly:     false,
};

interface MobileContextType {
  viewMode:      ViewMode;
  setViewMode:   (mode: ViewMode) => void;
  activeTab:     ActiveTab;
  setActiveTab:  (tab: ActiveTab) => void;
  ottoOpen:     boolean;
  setOttoOpen:  (open: boolean) => void;
  filtersOpen:   boolean;
  setFiltersOpen:(open: boolean) => void;
  qrOpen:        boolean;
  setQrOpen:     (open: boolean) => void;
  filters:       MobileFilters;
  setFilters:    (filters: MobileFilters) => void;
  updateFilter: <K extends keyof MobileFilters>(key: K, value: MobileFilters[K]) => void;
  resetFilters:  () => void;
  // Sous-filtres
  jobSubFilter:     JobSubFilter;
  setJobSubFilter:  (f: JobSubFilter) => void;
  dispoSubFilter:   DispoSubFilter;
  setDispoSubFilter:(f: DispoSubFilter) => void;
  // Filtres sauvegardés
  savedFilters:        SavedFilter[];
  saveCurrentFilters:  (name: string) => void;
  applySavedFilter:    (index: number) => void;
  deleteSavedFilter:   (index: number) => void;
  setDefaultFilter:    (index: number) => void;
}

const MobileContext = createContext<MobileContextType | null>(null);

/** Clé du sac `users.preferences` (whitelist côté serveur dans users.setPref). */
const PREF_KEY = 'savedFilters';

export function MobileProvider({ children }: { children: ReactNode }) {
  const [viewMode,       setViewMode]       = useState<ViewMode>('map');
  const [activeTab,      setActiveTab]      = useState<ActiveTab>('prestataires');
  const [ottoOpen,      setOttoOpen]      = useState(false);
  const [filtersOpen,    setFiltersOpen]    = useState(false);
  const [qrOpen,         setQrOpen]         = useState(false);
  const [jobSubFilter,   setJobSubFilter]   = useState<JobSubFilter>('all');
  const [dispoSubFilter, setDispoSubFilter] = useState<DispoSubFilter>('all');
  const [filters, setFilters] = useState<MobileFilters>(DEFAULT_FILTERS);
  const [savedFilters, setSavedFilters] = useState<SavedFilter[]>([]);

  // Filtres sauvegardés : sur le COMPTE (29/08), plus sur l'appareil — le même
  // compte ouvert sur deux téléphones retrouve ses recherches enregistrées.
  const prefsQ = trpc.users.prefs.useQuery(undefined, { retry: false, staleTime: 60_000 });
  const setPrefMut = trpc.users.setPref.useMutation();
  // Bascule à true une fois l'hydratation serveur reçue — évite d'écraser le
  // compte avec un tableau vide pendant le tout premier rendu.
  const hydrated = useRef(false);

  useEffect(() => {
    if (!prefsQ.data || hydrated.current) return;
    hydrated.current = true;
    const raw = (prefsQ.data as Record<string, unknown>)[PREF_KEY];
    const list = Array.isArray(raw) ? (raw as SavedFilter[]) : [];
    setSavedFilters(list);
    const def = list.find(s => s.isDefault);
    if (def) setFilters(def.filters);
  }, [prefsQ.data]);

  useEffect(() => {
    if (!hydrated.current) return; // pas avant la première hydratation
    setPrefMut.mutate({ key: PREF_KEY, value: savedFilters });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedFilters]);

  const updateFilter = useCallback(<K extends keyof MobileFilters>(
    key: K,
    value: MobileFilters[K],
  ) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  const resetFilters = useCallback(() => setFilters(DEFAULT_FILTERS), []);

  const saveCurrentFilters = useCallback((name: string) => {
    setSavedFilters(prev => [
      ...prev,
      { name: name.trim() || `Recherche ${prev.length + 1}`, filters, isDefault: false, savedAt: new Date().toISOString() },
    ]);
  }, [filters]);

  const applySavedFilter = useCallback((index: number) => {
    setSavedFilters(prev => {
      const item = prev[index];
      if (item) setFilters(item.filters);
      return prev;
    });
  }, []);

  const deleteSavedFilter = useCallback((index: number) => {
    setSavedFilters(prev => prev.filter((_, i) => i !== index));
  }, []);

  const setDefaultFilter = useCallback((index: number) => {
    setSavedFilters(prev => prev.map((f, i) => ({ ...f, isDefault: i === index })));
  }, []);

  return (
    <MobileContext.Provider value={{
      viewMode,    setViewMode,
      activeTab,   setActiveTab,
      ottoOpen,   setOttoOpen,
      filtersOpen, setFiltersOpen,
      qrOpen,      setQrOpen,
      filters,     setFilters, updateFilter, resetFilters,
      jobSubFilter, setJobSubFilter,
      dispoSubFilter, setDispoSubFilter,
      savedFilters, saveCurrentFilters, applySavedFilter, deleteSavedFilter, setDefaultFilter,
    }}>
      {children}
    </MobileContext.Provider>
  );
}

export function useMobile(): MobileContextType {
  const ctx = useContext(MobileContext);
  if (!ctx) throw new Error('useMobile must be used within <MobileProvider>');
  return ctx;
}

/** Version sûre — retourne null si hors MobileProvider (ex: pages desktop). */
export function useMobileSafe(): MobileContextType | null {
  return useContext(MobileContext);
}
